import { IAppState } from 'spw-redux/reducer';
import {
  createSelector,
  createSelectorCreator,
  defaultMemoize,
} from 'reselect';
import { TOTAL_PROGRAM_TYPE } from 'spw-constants';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import TotalProgramSummaryCalculator from '../../../models/TotalProgramSummaryCalculator';
import ProgramSummaryCalculator, {
  SummaryCalculator,
} from '../../../models/ProgramSummaryCalculator';

const worksheetHasNotChanged = (a: any, b: any) => {
  return (
    !!a &&
    !!b &&
    a.subtotalProgramUSF === b.subtotalProgramUSF &&
    a.allCategoriesSeats === b.allCategoriesSeats
  );
};

const createMemoizedWorksheetSelector = createSelectorCreator(
  defaultMemoize,
  worksheetHasNotChanged
);

export const selectIsEditingSavedScenario = (state: IAppState) =>
  state.calculatorReducer.isEditingSavedScenario;

export const selectCurrentScenario = (state: IAppState) =>
  state.calculatorReducer.currentScenario;

export const selectScenariosRequestStatus = (state: IAppState) =>
  state.calculatorReducer.scenariosRequestStatus;

export const selectTotalProgramsRequestStatus = (state: IAppState) =>
  state.calculatorReducer.totalProgramsRequestStatus;
export const selectWorksheetsRequestStatus = (state: IAppState) =>
  state.calculatorReducer.worksheetsRequestStatus;

export const selectWorksheet = (state: IAppState) =>
  state.calculatorReducer.worksheet;

export const selectScenarios = (state: IAppState) =>
  state.calculatorReducer.scenarios;

export const selectTotalPrograms = (state: IAppState) =>
  state.calculatorReducer.totalPrograms;

export const selectIsCalculatorDirty = (state: IAppState) =>
  state.calculatorReducer.isCalculatorDirty;

export const selectWorksheets = (state: IAppState) =>
  state.calculatorReducer.worksheets;

export const selectTpsWorksheets = (state: IAppState) =>
  state.calculatorReducer.tpsWorksheetsEnhanced;

export const selectMemoizedWorksheet = createMemoizedWorksheetSelector(
  selectWorksheet,
  (worksheet) => {
    return worksheet;
  }
);

export const selectProgramSummaryCalculator = createSelector(
  selectCurrentScenario,
  selectMemoizedWorksheet,
  selectTpsWorksheets,
  (scenario, worksheet, totalProgramWorksheets): SummaryCalculator | null => {
    if (scenario?.calculatorType === TOTAL_PROGRAM_TYPE) {
      const summaryCalculator = new TotalProgramSummaryCalculator(
        scenario,
        totalProgramWorksheets
      ).toPlainObject();

      return summaryCalculator;
    }

    return new ProgramSummaryCalculator(scenario, worksheet).toPlainObject();
  }
);

export const selectAllChildWorksheetSpaces = (state: IAppState) =>
  state.calculatorReducer.childWorksheetSpaces;

export const selectTemporaryScenario = (state: IAppState) =>
  state.calculatorReducer.temporaryScenario;

export const selectChildWorksheetSpacesRequestStatus = (state: IAppState) =>
  state.calculatorReducer.childWorksheetSpacesRequestStatus;

export const selectWorksheetRequestStatus = (state: IAppState) =>
  state.calculatorReducer.worksheetRequestStatus;

export const selectIsSavingScenario = (state: IAppState) =>
  state.calculatorReducer.isSavingScenario;

export const selectShowWorksheetUpdateDialog = (state: IAppState) =>
  state.calculatorReducer.showWorksheetUpdateDialog;

export const selectIsRowDisabled = createSelector(
  selectCurrentScenario,
  selectCurrentUserIsViewOnly,
  selectShowWorksheetUpdateDialog,
  (scenario, currentUserIsViewOnly, isShowingWorksheetUpdateDialog) => {
    return (
      scenario.calculatorType === TOTAL_PROGRAM_TYPE ||
      currentUserIsViewOnly ||
      isShowingWorksheetUpdateDialog
    );
  }
);

export const selectScenarioListType = (state: IAppState) =>
  state.calculatorReducer.scenarioListType;

export const selectCurrentCalculatorType = (state: IAppState) =>
  state.calculatorReducer.currentCalculatorType;

export const selectShowScenarioDialog = (state: IAppState) =>
  state.calculatorReducer.showScenarioDialog;
